import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scissors, Calendar, MapPin, AlertCircle, Grid, List, ArrowLeft, Layers, Plus, Box, Search, X, Zap, QrCode, Printer, Archive, ChevronDown, ChevronUp, ShieldAlert, FileDown, Scale, Bug, Crown, Heart, Users, Thermometer, Egg, Activity, ArrowUpDown, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import PageTourButton from '../PageTourButton';
import { toast } from 'sonner';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { usePendingSync } from '../OfflineSyncChip';
import { splitStage } from '../../utils/splitMethods';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_HIVE_IMG = 'https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=80&h=80&fit=crop&crop=center';

const HiveThumb = ({ hiveImage }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!hiveImage || !hiveImage.includes('/')) { setSrc(DEFAULT_HIVE_IMG); return; }
    let cancelled = false;
    const token = localStorage.getItem('token') || localStorage.getItem('crewToken');
    axios.get(`${API}/files/${hiveImage}`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' })
      .then(res => { if (!cancelled) setSrc(URL.createObjectURL(res.data)); })
      .catch(() => { if (!cancelled) setSrc(DEFAULT_HIVE_IMG); });
    return () => { cancelled = true; };
  }, [hiveImage]);
  return (
    <div className="w-14 h-14 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
      <img src={src || DEFAULT_HIVE_IMG} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.src = DEFAULT_HIVE_IMG; }} />
    </div>
  );
};

// ── Border stripe color by health score ──
const STRIPE = { green: 'border-l-green-500', amber: 'border-l-amber-500', red: 'border-l-red-500' };
const BADGE_CLS = {
  green: 'bg-green-50 text-green-700', amber: 'bg-amber-50 text-amber-700', red: 'bg-red-50 text-red-700',
  ready: 'bg-sky-50 text-sky-700', inactive: 'bg-stone-100 text-stone-600', afb: 'bg-red-100 text-red-700',
};
const BADGE_LABEL = { green: 'Healthy', amber: 'Watch', red: 'Alert!' };

const HivesList = ({
  hives = [], yards = [], selectedYard, onYardChange, viewMode = 'grid', onViewModeChange,
  title = 'All Hives', defaultGroupByYard = true, onAddHive, onBulkAction
}) => {
  const navigate = useNavigate();
  const pendingItems = usePendingSync();
  const [groupByYard] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [printingQR, setPrintingQR] = useState(false);
  const [filterSupers, setFilterSupers] = useState(false);
  const [showDestroyedHives, setShowDestroyedHives] = useState(false);
  const [confirmDestroyId, setConfirmDestroyId] = useState(null);
  const [destroyDate, setDestroyDate] = useState(new Date().toISOString().split('T')[0]);
  const [destroyNotes, setDestroyNotes] = useState('');
  const [inspSummaries, setInspSummaries] = useState({});
  const [sortBy, setSortBy] = useState('number');
  const [hiveSponsors, setHiveSponsors] = useState({});
  const [hiveSplits, setHiveSplits] = useState({});
  const [splitFilter, setSplitFilter] = useState('all');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    if (hives.length) {
      axios.get(`${API}/hives/latest-inspections`, { headers })
        .then(res => setInspSummaries(res.data || {}))
        .catch(() => {});
    }
    // Fetch hive sponsors for badges
    axios.get(`${API}/donations/hive-sponsors`)
      .then(res => setHiveSponsors(res.data?.sponsors || {}))
      .catch(() => {});
    // Active splits per hive (newest first), keyed by the hive holding the split
    if (token) {
      axios.get(`${API}/hive-splits`, { headers })
        .then(res => {
          const map = {};
          (res.data || []).forEach(sp => {
            const st = splitStage(sp);
            const holder = sp.destination_hive_id || sp.parent_hive_id;
            if (st && holder && !map[holder]) map[holder] = st;
          });
          setHiveSplits(map);
        })
        .catch(() => {});
    }
  }, [hives]);

  const activeHives = useMemo(() => hives.filter(h => h.status !== 'destroyed'), [hives]);
  const destroyedHives = useMemo(() => hives.filter(h => h.status === 'destroyed'), [hives]);

  const handlePrintAllQRCodes = async () => {
    const hivesToPrint = filteredHives.filter(h => h.id);
    if (hivesToPrint.length === 0) { toast.error('No hives to print'); return; }
    setPrintingQR(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/hives/qr-codes-pdf`, { hive_ids: hivesToPrint.map(h => h.id) }, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a'); link.href = url;
      link.download = selectedYard ? `qr_codes_${selectedYard.replace(/\s+/g, '_')}.pdf` : 'all_hives_qr_codes.pdf';
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`QR codes PDF generated for ${hivesToPrint.length} hives`);
    } catch { toast.error('Failed to generate QR codes PDF'); }
    finally { setPrintingQR(false); }
  };

  // Filter
  const filteredHives = useMemo(() => {
    let result = selectedYard ? activeHives.filter(h => h.yard === selectedYard) : activeHives;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      if (q === 'destroyed') return [];
      result = result.filter(h => String(h.hive_number || '').toLowerCase().includes(q) || (h.nickname || '').toLowerCase().includes(q));
    }
    if (filterSupers) {
      result = result.filter(h => { const ec = h?.equipment_config || {}; return Object.entries(ec).some(([k, v]) => k.startsWith('super_') && v > 0); });
    }
    if (splitFilter !== 'all') {
      result = result.filter(h => hiveSplits[h.id] && (splitFilter === 'any' || hiveSplits[h.id].stage === splitFilter));
    }
    return result;
  }, [activeHives, selectedYard, searchQuery, filterSupers, splitFilter, hiveSplits]);

  // Sort
  const sortedHives = useMemo(() => {
    const arr = [...filteredHives];
    const getInsp = (h) => inspSummaries[h.id] || {};
    const healthOrder = { red: 0, amber: 1, green: 2, '': 3 };
    switch (sortBy) {
      case 'health': return arr.sort((a, b) => (healthOrder[getInsp(a).health_score || ''] || 3) - (healthOrder[getInsp(b).health_score || ''] || 3));
      case 'mite': return arr.sort((a, b) => (parseFloat(getInsp(b).mite_percentage || 0)) - (parseFloat(getInsp(a).mite_percentage || 0)));
      case 'inspected': return arr.sort((a, b) => {
        const da = a.last_inspection ? new Date(a.last_inspection).getTime() : 0;
        const db = b.last_inspection ? new Date(b.last_inspection).getTime() : 0;
        return da - db; // Oldest first
      });
      default: return arr.sort((a, b) => (a.hive_number || 0) - (b.hive_number || 0));
    }
  }, [filteredHives, sortBy, inspSummaries]);

  // Group by yard
  const groupedHives = useMemo(() => {
    if (!groupByYard) return null;
    const groups = {};
    sortedHives.forEach(hive => { const yard = hive.yard || 'Unassigned'; if (!groups[yard]) groups[yard] = []; groups[yard].push(hive); });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [sortedHives, groupByYard]);

  // ── Urgent alerts banner ──
  const urgentAlerts = useMemo(() => {
    const alerts = [];
    sortedHives.forEach(h => {
      const insp = inspSummaries[h.id] || {};
      if (insp.swarm_conditions) alerts.push(`Hive ${h.hive_number} is showing swarm conditions`);
      if (insp.health_score === 'red' && (insp.diseases?.length > 0)) alerts.push(`Hive ${h.hive_number} has disease: ${insp.diseases.join(', ')}`);
      if (insp.health_score === 'red' && insp.brood_alert === 'warning') alerts.push(`Hive ${h.hive_number} — ${insp.brood_status}`);
    });
    return alerts;
  }, [sortedHives, inspSummaries]);

  // ── fmt helpers ──
  const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }); } catch { return '—'; } };

  // ── HIVE CARD ──
  const renderHiveCard = (hive) => {
    const pendingSync = pendingItems.some(i => (i.hiveIds || []).includes(hive.id));
    const syncTag = pendingSync && <span data-testid={`hive-pending-sync-${hive.hive_number}`} className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 font-semibold whitespace-nowrap">Pending sync</span>;
    const sp = hiveSplits[hive.id];
    const splitTag = sp && (
      <button type="button" onClick={(e) => { e.stopPropagation(); navigate(`/?view=hive-detail&hiveId=${hive.id}&focus=splits`); }}
        data-testid={`hive-split-badge-${hive.hive_number}`} title="Open this hive's split tracker"
        className={`ml-1 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap align-middle ${sp.stage === 'waiting' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-800'}`}>
        <Scissors className="h-2.5 w-2.5" />{sp.label}
      </button>
    );
    const insp = inspSummaries[hive.id] || {};
    const hasInsp = Object.keys(insp).length > 1;
    const hs = insp.health_score || '';
    const stripe = hive.status === 'ready' ? 'border-l-sky-400' : hive.status === 'inactive' ? 'border-l-stone-400' : (STRIPE[hs] || 'border-l-stone-300');
    const badgeCls = hive.status === 'ready' ? BADGE_CLS.ready : hive.status === 'inactive' ? ((hive.inactive_reason || '').toLowerCase().includes('afb') ? BADGE_CLS.afb : BADGE_CLS.inactive) : (BADGE_CLS[hs] || '');
    const badgeLabel = hive.status === 'ready' ? 'Ready' : hive.status === 'inactive' ? ((hive.inactive_reason || '').toLowerCase().includes('afb') ? 'AFB' : 'Inactive') : (BADGE_LABEL[hs] || '');

    const inspDate = hive.last_inspection ? fmtDate(hive.last_inspection) : null;

    const broodText = insp.brood_status || '';
    const broodCls = broodText === 'BIAS' ? 'text-green-700 font-semibold' : insp.brood_alert === 'warning' ? 'text-red-600 font-semibold' : insp.brood_alert === 'caution' ? 'text-amber-600 font-semibold' : 'text-slate-700';
    const resources = [insp.nectar_level && `N:${insp.nectar_level}`, insp.pollen_level && `P:${insp.pollen_level}`, insp.honey_level && `H:${insp.honey_level}`].filter(Boolean).join(' · ');

    // Warning pills
    const warnings = [];
    if (insp.swarm_conditions) warnings.push({ text: 'Swarm Conditions!', cls: 'bg-red-50 text-red-700' });
    if (insp.diseases?.length > 0) warnings.push({ text: insp.diseases.join(', '), cls: 'bg-red-50 text-red-700' });
    if (insp.pests?.length > 0) warnings.push({ text: insp.pests.join(', '), cls: 'bg-amber-50 text-amber-700' });

    // ── LIST VIEW ──
    if (viewMode === 'list') {
      return (
        <div key={hive.id} role="button" tabIndex={0} onClick={() => navigate(`/?view=hive-detail&hiveId=${hive.id}`)}
          className={`bg-white rounded-lg shadow-sm border-l-4 ${stripe} p-3 hover:shadow-md transition-all cursor-pointer`} data-testid={`hive-card-${hive.hive_number}`}>
          <div className="flex items-center gap-3 flex-wrap">
            <HiveThumb hiveImage={hive.hive_image} />
            <div className="flex items-center gap-2 min-w-0 w-32 flex-shrink-0">
              <span className="font-bold text-slate-800 text-sm">Hive {hive.hive_number}</span>{syncTag}{splitTag}
              {hive.nickname && <span className="text-xs text-slate-400 italic truncate">&quot;{hive.nickname}&quot;</span>}
            </div>
            {badgeLabel && <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${badgeCls} flex-shrink-0`}>{badgeLabel}</span>}
            <div className="flex-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
              {inspDate && <span className="flex items-center gap-1"><Calendar className="h-3 w-3 text-slate-400" />{inspDate}</span>}
              {!inspDate && hive.status === 'active' && <span className="text-slate-400 italic text-[10px]">Not inspected</span>}
              {insp.weight && <span className="flex items-center gap-1"><Scale className="h-3 w-3 text-blue-400" />{insp.weight}kg</span>}
              {!insp.weight && insp.heft_weight && <span className={`flex items-center gap-1 ${insp.heft_weight === 'Heavy' ? 'text-green-600' : insp.heft_weight === 'Medium' ? 'text-amber-600' : 'text-red-600'}`}><Scale className="h-3 w-3 text-blue-400" />{insp.heft_weight}</span>}
              {(insp.mite_count || insp.mite_percentage) && <span className="flex items-center gap-1"><Bug className="h-3 w-3 text-red-400" />{insp.mite_count}{insp.mite_percentage ? ` (${insp.mite_percentage}%)` : ''}</span>}
              {insp.population && <span className="flex items-center gap-1"><Users className="h-3 w-3 text-amber-500" />{insp.population}</span>}
              {broodText && <span className={`flex items-center gap-1 ${broodCls}`}><Egg className="h-3 w-3 text-yellow-500" />{broodText}</span>}
              {hasInsp && insp.queen_spotted !== undefined && <span className="flex items-center gap-1"><Crown className="h-3 w-3 text-purple-400" />{insp.queen_spotted ? <span className="text-green-600">Seen</span> : <span className="text-slate-400">Not Seen</span>}</span>}
              {insp.pattern?.length > 0 && <span className="flex items-center gap-1"><Activity className="h-3 w-3 text-indigo-400" />{insp.pattern.join(', ')}</span>}
              {resources && <span className="flex items-center gap-1"><Layers className="h-3 w-3 text-teal-400" />{resources}</span>}
            </div>
            {warnings.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {warnings.map((w, i) => <span key={i} className={`${w.cls} text-[10px] px-2 py-0.5 rounded font-medium flex items-center gap-1`}><AlertTriangle className="h-3 w-3" />{w.text}</span>)}
              </div>
            )}
            {hiveSponsors[hive.id] && (
              <span className="flex items-center gap-1 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 text-[10px] text-amber-700 flex-shrink-0">
                <Heart className="h-3 w-3 text-amber-500" />Sponsored by <strong>{hiveSponsors[hive.id].donor_name}</strong>
              </span>
            )}
          </div>
        </div>
      );
    }

    // ── GRID CARD ──
    return (
      <div key={hive.id} role="button" tabIndex={0} onClick={() => navigate(`/?view=hive-detail&hiveId=${hive.id}`)}
        className={`bg-white rounded-xl shadow-sm border-l-4 ${stripe} p-3 hover:shadow-md transition-all cursor-pointer flex gap-3`} data-testid={`hive-card-${hive.hive_number}`}>
        <HiveThumb hiveImage={hive.hive_image} />
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-start">
            <div className="min-w-0">
              <h4 className="font-bold text-slate-800 text-sm leading-tight truncate">Hive {hive.hive_number}{syncTag}</h4>
              {splitTag && <div className="-ml-1 mt-0.5">{splitTag}</div>}
              {hive.nickname && <span className="text-[10px] text-slate-400 italic">&quot;{hive.nickname}&quot;</span>}
            </div>
            {badgeLabel && <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${badgeCls}`}>{badgeLabel}</span>}
          </div>

          {/* 2-Column Metric Grid */}
          {hive.status === 'active' && hasInsp && (
            <div className="grid grid-cols-2 gap-x-3 gap-y-[2px] mt-1.5 border-t border-slate-100 pt-1.5 text-[10px]">
              {inspDate && <div className="text-slate-500"><Calendar className="h-3 w-3 inline mr-0.5 text-slate-400" />{inspDate}</div>}
              {insp.queen_spotted !== undefined && <div className="text-slate-500 text-right"><Crown className="h-3 w-3 inline mr-0.5 text-purple-400" /><span className={insp.queen_spotted ? 'font-medium text-green-600' : 'text-slate-400'}>{insp.queen_spotted ? 'Seen' : 'Not Seen'}</span></div>}
              {insp.weight && <div className="text-slate-500"><Scale className="h-3 w-3 inline mr-0.5 text-blue-400" /><span className="font-medium text-slate-700">{insp.weight} kg</span></div>}
              {!insp.weight && insp.heft_weight && <div className="text-slate-500"><Scale className="h-3 w-3 inline mr-0.5 text-blue-400" /><span className={`font-medium ${insp.heft_weight === 'Heavy' ? 'text-green-600' : insp.heft_weight === 'Medium' ? 'text-amber-600' : 'text-red-600'}`}>{insp.heft_weight}</span></div>}
              {insp.temperament && <div className="text-slate-500 text-right"><Thermometer className="h-3 w-3 inline mr-0.5 text-orange-400" /><span className="font-medium text-slate-700">{insp.temperament}</span></div>}
              {(insp.mite_count || insp.mite_percentage) && <div className="text-slate-500"><Bug className="h-3 w-3 inline mr-0.5 text-red-400" /><span className="font-medium text-slate-700">{insp.mite_count}{insp.mite_percentage ? ` (${insp.mite_percentage}%)` : ''}</span></div>}
              {insp.population && <div className="text-slate-500 text-right"><Users className="h-3 w-3 inline mr-0.5 text-amber-500" /><span className="font-medium text-slate-700">{insp.population}</span></div>}
              {broodText && <div className={`col-span-2 text-slate-500`}><Egg className="h-3 w-3 inline mr-0.5 text-yellow-500" />Brood: <span className={broodCls}>{broodText}</span></div>}
              {insp.pattern?.length > 0 && <div className="text-slate-500 col-span-2"><Activity className="h-3 w-3 inline mr-0.5 text-indigo-400" />Pattern: <span className="font-medium text-slate-700">{insp.pattern.join(', ')}</span></div>}
              {resources && <div className="text-slate-500 col-span-2"><Layers className="h-3 w-3 inline mr-0.5 text-teal-400" />{resources}</div>}
            </div>
          )}
          {hive.status === 'active' && !hasInsp && (
            <div className="text-[10px] text-slate-400 mt-1.5 border-t border-slate-100 pt-1.5 italic">Not inspected yet</div>
          )}

          {/* Warning pills */}
          {warnings.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {warnings.map((w, i) => <span key={i} className={`${w.cls} text-[10px] px-1.5 py-0.5 rounded font-medium flex items-center gap-0.5`}><AlertTriangle className="h-2.5 w-2.5" />{w.text}</span>)}
            </div>
          )}

          {/* Sponsor badge */}
          {hiveSponsors[hive.id] && (
            <div className="mt-1.5 flex items-center gap-1 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1">
              <Heart className="h-3 w-3 text-amber-500 flex-shrink-0" />
              <span className="text-[10px] text-amber-700 truncate">Sponsored by <strong>{hiveSponsors[hive.id].donor_name}</strong></span>
            </div>
          )}

          {/* Status pills for non-active */}
          {hive.status === 'inactive' && (hive.inactive_reason || '').toLowerCase().includes('afb') && (
            <div className="mt-1.5">
              <span className="text-[10px] bg-red-600 text-white px-2 py-0.5 rounded-full font-bold">AFB — BURN EQUIPMENT</span>
              <button onClick={(e) => { e.stopPropagation(); setConfirmDestroyId(hive.id); }} className="block w-full text-[10px] bg-red-100 hover:bg-red-200 text-red-800 px-2 py-1 rounded font-medium mt-1" data-testid={`confirm-destroyed-${hive.id}`}>
                <ShieldAlert className="h-3 w-3 inline mr-1" />Confirm Destroyed
              </button>
            </div>
          )}
          {!hive.yard && hive.status !== 'inactive' && (
            <span className="inline-block mt-1.5 text-[10px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">No apiary assigned</span>
          )}
        </div>
      </div>
    );
  };

  const gridCls = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5';
  const listCls = 'space-y-2';

  return (
    <div className="bg-white rounded-xl border border-stone-200 shadow-sm" data-testid="hives-list">
      {/* Header */}
      <div className="p-4 border-b border-stone-100 flex flex-col gap-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            {selectedYard && (
              <Button variant="ghost" size="sm" onClick={() => { onYardChange(null); }} className="h-8 px-2" data-testid="back-to-hives-btn">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <h2 className="font-bold text-black text-lg">{selectedYard ? `Hives in ${selectedYard}` : title}</h2>
            <span className="text-stone-500 text-sm">({filteredHives.length})</span>
          </div>
          {selectedYard && (() => { const yd = yards.find(y => y.name === selectedYard); return yd?.location ? <div className="text-sm text-stone-500 flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-stone-400" />{yd.location}</div> : null; })()}
          <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
            <PageTourButton pageView="hives" />
            {filteredHives.length > 0 && (
              <Button onClick={handlePrintAllQRCodes} disabled={printingQR} variant="outline" className="border-teal-300 text-teal-700 hover:bg-teal-50 h-8 text-xs px-2 sm:px-3" data-testid="print-all-qr-btn">
                <QrCode className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">{printingQR ? 'Generating...' : 'Print QR Codes'}</span>
              </Button>
            )}
            <Button onClick={() => { const t = localStorage.getItem('token'); window.open(`${API}/hive-reports/bulk-pdf?token=${t}`, '_blank'); toast.success('Generating PDF report...'); }} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50 h-8 text-xs px-2 sm:px-3" data-testid="bulk-pdf-btn">
              <FileDown className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Export PDF</span>
            </Button>
            {selectedYard && filteredHives.length > 0 && onBulkAction && (
              <Button onClick={() => onBulkAction(selectedYard, filteredHives.filter(h => h.status === 'active'))} className="bg-purple-500 hover:bg-purple-600 text-white h-8 text-xs px-2 sm:px-3" data-testid="bulk-yard-action-btn">
                <Zap className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Apply to All</span>
              </Button>
            )}
            <Button onClick={onAddHive} className="bg-amber-500 hover:bg-amber-600 text-black h-8 text-xs px-2 sm:px-3" data-testid="add-hive-btn">
              <Plus className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Add Hive</span>
            </Button>
            <Button onClick={() => navigate('/swarm-traps')} variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50 h-8 text-xs px-2 sm:px-3" data-testid="swarm-traps-btn">
              <Box className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Swarm Traps</span>
            </Button>
            <div className="flex items-center gap-1">
              <Button variant={viewMode === 'grid' ? 'default' : 'ghost'} size="sm" onClick={() => onViewModeChange?.('grid')} className={`h-8 w-8 p-0 ${viewMode === 'grid' ? 'bg-amber-500 text-black' : ''}`}><Grid className="h-4 w-4" /></Button>
              <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="sm" onClick={() => onViewModeChange?.('list')} className={`h-8 w-8 p-0 ${viewMode === 'list' ? 'bg-amber-500 text-black' : ''}`}><List className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>

        {/* Search + Filters */}
        <div className="flex gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input type="text" placeholder="Search by hive # or nickname..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 pr-9 h-10 bg-stone-50 border-stone-200 rounded-lg" data-testid="hive-search-input" />
            {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"><X className="h-4 w-4" /></button>}
          </div>
          <Button variant={filterSupers ? 'default' : 'outline'} size="sm" onClick={() => setFilterSupers(!filterSupers)}
            className={`h-10 text-xs whitespace-nowrap ${filterSupers ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'border-blue-300 text-blue-700 hover:bg-blue-50'}`} data-testid="filter-supers-btn">
            <Layers className="h-3.5 w-3.5 mr-1" />Has Supers
          </Button>
          <Select value={splitFilter} onValueChange={setSplitFilter}>
            <SelectTrigger className={`h-10 w-[190px] text-xs ${splitFilter !== 'all' ? 'border-amber-400 bg-amber-50 text-amber-800' : 'border-stone-200'}`} data-testid="split-filter-dropdown">
              <Scissors className="h-3.5 w-3.5 mr-1 text-amber-600" />
              <SelectValue placeholder="Splits" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All hives</SelectItem>
              <SelectItem value="any">Any active split</SelectItem>
              <SelectItem value="steps">Still doing steps</SelectItem>
              <SelectItem value="waiting">Waiting on queen result</SelectItem>
            </SelectContent>
          </Select>
          {/* Sort dropdown */}
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-10 w-[170px] text-xs border-stone-200" data-testid="sort-dropdown">
              <ArrowUpDown className="h-3.5 w-3.5 mr-1 text-stone-400" />
              <SelectValue placeholder="Sort by..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="number">Hive Number</SelectItem>
              <SelectItem value="health">Health Score</SelectItem>
              <SelectItem value="mite">Mite Load</SelectItem>
              <SelectItem value="inspected">Days Since Inspection</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Urgent Alerts Banner */}
      {urgentAlerts.length > 0 && (
        <div className="mx-4 mt-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800" data-testid="urgent-alerts">
          <AlertTriangle className="h-3.5 w-3.5 inline mr-1 text-amber-600" />
          <span className="font-semibold">Urgent Attention Required: </span>
          {urgentAlerts.slice(0, 3).join(', and ')}
          {urgentAlerts.length > 3 && `, and ${urgentAlerts.length - 3} more`}
        </div>
      )}

      {/* Content */}
      <div className="p-4">
        {filteredHives.length === 0 ? (
          <div className="text-center py-8 text-stone-500">{searchQuery ? `No hives matching "${searchQuery}"` : selectedYard ? `No hives in ${selectedYard}` : 'No hives yet'}</div>
        ) : groupByYard && groupedHives ? (
          <div className="space-y-6">
            {groupedHives.map(([yardName, yardHives]) => {
              const yardData = yards.find(y => y.name === yardName || y.name?.trim() === yardName);
              return (
                <div key={yardName}>
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b-2 border-amber-300 cursor-pointer hover:bg-amber-50 -mx-4 px-4 py-2 transition-colors" onClick={() => onYardChange?.(yardName === 'Unassigned' ? null : yardName)}>
                    <MapPin className="h-5 w-5 text-amber-600 flex-shrink-0" />
                    <span className="font-bold text-black text-base">{yardName}</span>
                    <span className="text-stone-500 text-sm">({yardHives.length} hives)</span>
                    {yardData?.location && <span className="text-stone-500 text-xs ml-auto truncate max-w-[50%] hidden sm:inline">{yardData.location}</span>}
                    {onBulkAction && yardName !== 'Unassigned' && yardHives.filter(h => h.status === 'active').length > 0 && (
                      <Button onClick={(e) => { e.stopPropagation(); onBulkAction(yardName, yardHives.filter(h => h.status === 'active')); }} className="bg-purple-500 hover:bg-purple-600 text-white h-7 text-xs px-2 ml-2 flex-shrink-0">
                        <Zap className="h-3 w-3 mr-1" />Apply to All
                      </Button>
                    )}
                  </div>
                  {yardData?.location && <div className="text-xs text-stone-500 -mt-2 mb-3 pl-7 sm:hidden truncate">{yardData.location}</div>}
                  <div className={viewMode === 'grid' ? gridCls : listCls}>
                    {yardHives.map(h => renderHiveCard(h))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={viewMode === 'grid' ? gridCls : listCls}>{sortedHives.map(h => renderHiveCard(h))}</div>
        )}
      </div>

      {/* Destroyed Hives */}
      {destroyedHives.length > 0 && (
        <div className="border-t border-stone-200" data-testid="destroyed-hives-section">
          <button onClick={() => setShowDestroyedHives(!showDestroyedHives)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-stone-50" data-testid="toggle-destroyed-hives">
            <div className="flex items-center gap-2"><Archive className="h-4 w-4 text-stone-400" /><span className="text-sm font-medium text-stone-600">Destroyed Hives ({destroyedHives.length})</span></div>
            {showDestroyedHives ? <ChevronUp className="h-4 w-4 text-stone-500" /> : <ChevronDown className="h-4 w-4 text-stone-400" />}
          </button>
          {showDestroyedHives && (
            <div className="px-4 pb-4">
              <div className={viewMode === 'grid' ? gridCls : listCls}>
                {destroyedHives.map(hive => (
                  <button key={hive.id} onClick={() => navigate(`/?view=hive-detail&hiveId=${hive.id}`)} className="bg-stone-50 border-l-4 border-l-stone-400 rounded-xl p-3 text-left hover:shadow-md transition-all opacity-75 flex gap-3 w-full" data-testid={`destroyed-hive-${hive.hive_number}`}>
                    <HiveThumb hiveImage={hive.hive_image} />
                    <div>
                      <div className="font-bold text-stone-600 text-sm">Hive {hive.hive_number}</div>
                      {hive.nickname && <div className="text-xs text-stone-400 italic">&quot;{hive.nickname}&quot;</div>}
                      <span className="text-[10px] text-stone-500">Destroyed {hive.destroyed_at ? fmtDate(hive.destroyed_at) : ''}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Confirm Destroy Dialog */}
      {confirmDestroyId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setConfirmDestroyId(null)}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-red-800">Confirm Hive Destroyed</h3>
            <p className="text-sm text-stone-600">Record that Hive #{hives.find(h => h.id === confirmDestroyId)?.hive_number} has been destroyed.</p>
            <div><label className="text-xs font-medium text-stone-500">Date Destroyed</label><Input type="date" value={destroyDate} onChange={e => setDestroyDate(e.target.value)} /></div>
            <div><label className="text-xs font-medium text-stone-500">Notes</label><Input value={destroyNotes} onChange={e => setDestroyNotes(e.target.value)} placeholder="Reason..." data-testid="destroy-notes-input" /></div>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setConfirmDestroyId(null)} data-testid="destroy-cancel-btn">Cancel</Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white" data-testid="destroy-confirm-btn" onClick={async () => {
                try {
                  const token = localStorage.getItem('token');
                  await axios.put(`${API}/hives/${confirmDestroyId}`, { status: 'destroyed', destroyed_at: destroyDate, destroyed_notes: destroyNotes }, { headers: { Authorization: `Bearer ${token}` } });
                  toast.success('Hive marked as destroyed');
                  setConfirmDestroyId(null);
                  window.dispatchEvent(new Event('refreshData'));
                } catch { toast.error('Failed'); }
              }}>Confirm Destroyed</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HivesList;
